import React, { ReactNode, useRef, useState } from 'react';
import styles from './FooterPartial.module.css';
import { useStickToVisualBottom } from '@/libs/hook';

import { getLinkBct, getSocialUrl } from '@/libs/app';
import {
  Link,
  Image,
  HomeIcon,
  HomeIconActive,
  ProductsIcon,
  ProductsIconActive,
  LiveStreamIcon,
  LiveStreamIconActive,
  NewsIcon,
  NewsIconActive,
  AccountIcon,
  AccountIconActive,
  Icon,
  ReelIcon,
  ReelIconActive,
} from '@/components';
import { useTranslation } from 'next-i18next';
import {
  SocialType,
  PagePath,
  journalPathForBlog,
  BctType,
  KeyActive,
  PlatForm,
} from '@/layouts/shared';
// Children remain in server-rendered HTML even when the mobile disclosure is closed.
const FooterGroup = ({ id, title, children }: { id: string; title: string; children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  return <section className={styles.group}>
    <h3 className={styles.desktopTitle}>{title}</h3>
    <button type="button" className={styles.mobileSummary} aria-expanded={open} aria-controls={id} onClick={() => setOpen((value) => !value)}>{title}<span aria-hidden="true">{open ? '−' : '+'}</span></button>
    <div id={id} className={styles.groupBody} data-expanded={open}>{children}</div>
  </section>;
};
import { MasterDataModel } from '@/models';
import { useMasterData } from '@/contexts/profile';
type FooterPartialProps = {
  isAuth: boolean;
  keyActive: string;
  isShowNav?: boolean;
  isHideFooterMobile?: boolean;
  platform: string;
};

const FooterPartial = ({
  keyActive,
  isAuth,
  isShowNav = true,
  isHideFooterMobile = false,
  platform,
}: FooterPartialProps) => {
  const { t } = useTranslation('layout');

  // Safari iOS: giữ menu dưới sát đáy vùng nhìn thấy khi cuộn (xem libs/hook)
  const mobileNavRef = useRef<HTMLDivElement>(null);
  useStickToVisualBottom(mobileNavRef, isShowNav && platform === PlatForm.Web);
  const masterData: MasterDataModel = useMasterData();

  const { theme } = masterData ?? {};
  const { logo_url, web_bg_footer_url, color_text_footer, app_menu_icon } =
    theme ?? {};

  const mobileNavItems = [
    {
      path: PagePath.HomePage,
      title: t('header.homepage'),
      target: '',
      icon: <HomeIcon />,
      iconActive: <HomeIconActive />,
      themeIcon: app_menu_icon?.icon_home_url,
      themeIconActive: app_menu_icon?.icon_home_selected_url,
      key: KeyActive.Home,
    },

    {
      path: PagePath.ProductsPage,
      title: t('header.product'),
      target: '',
      icon: <ProductsIcon />,
      iconActive: <ProductsIconActive />,
      themeIcon: app_menu_icon?.icon_product_url,
      themeIconActive: app_menu_icon?.icon_product_selected_url,
      key: KeyActive.Product,
    },
    {
      path: PagePath.LiveVideosPage,
      title: t('header.live-stream'),
      target: '',
      icon: <LiveStreamIcon />,
      iconActive: <LiveStreamIconActive />,
      themeIcon: app_menu_icon?.icon_live_url,
      themeIconActive: app_menu_icon?.icon_live_selected_url,
      key: KeyActive.LiveVideo,
    },
    {
      path: PagePath.ReelPage,
      title: t('header.reel'),
      target: '',
      icon: <ReelIcon />,
      iconActive: <ReelIconActive />,
      themeIcon: app_menu_icon?.icon_reel_url,
      themeIconActive: app_menu_icon?.icon_reel_selected_url,
      key: KeyActive.Reels,
    },
    {
      path: {
        pathname: journalPathForBlog(masterData?.menu_blogs?.[0]?.slug),
      },
      title: t('header.news'),
      target: '',
      icon: <NewsIcon />,
      iconActive: <NewsIconActive />,
      themeIcon: app_menu_icon?.icon_news_url,
      themeIconActive: app_menu_icon?.icon_news_selected_url,
      key: KeyActive.News,
    },
    {
      path: isAuth ? PagePath.AccountProfilePage : PagePath.LoginPage,
      title: t('header.account'),
      target: '',
      icon: <AccountIcon />,
      iconActive: <AccountIconActive />,
      themeIcon: app_menu_icon?.icon_acoount_url,
      themeIconActive: app_menu_icon?.icon_account_selected_url,
      key: KeyActive.Account,
    },
  ];
  const socialLinks = [
    { type: SocialType.Facebook, name: 'Facebook', image: '/facebook-color.svg' },
    { type: SocialType.TikTok, name: 'TikTok', image: '/tiktok.svg' },
    { type: SocialType.Youtube, name: 'YouTube', image: '/youtube.svg' },
    { type: SocialType.Instagram, name: 'Instagram', image: '/instagram.svg' },
  ];
  const legalStyle = {
    backgroundImage: web_bg_footer_url ? `url(${web_bg_footer_url})` : undefined,
    '--footer-legal-color': color_text_footer || '#758277',
    '--footer-legal-heading': color_text_footer || '#2b493b',
  } as React.CSSProperties;

  return (
    <>
      <footer className={`${styles.footer} ${platform === PlatForm.Mobile ? 'tw-hidden' : ''} ${isHideFooterMobile ? 'tw-hidden md:tw-block' : ''} ${isShowNav && platform === PlatForm.Web && keyActive !== KeyActive.GuideMeasuring ? styles.withMobileNav : ''}`}>
        <div className={`container ${styles.content}`}>
          <div className={styles.columns}>
            <section className={styles.contact} aria-label={t('footer.title.info')}>
              <Link url={PagePath.HomePage} title="An Thư The Diamond Store" className={styles.logoLink}>
                <Image src={logo_url || '/logo.svg'} alt="An Thư The Diamond Store" className={styles.logo} objectFit="contain" />
              </Link>
              <p className={styles.brandNote}>{t('footer.redesign.brandNote')}</p>
              <div className={styles.otherContacts}>
                <div><span>{t('footer.redesign.salesHotline')}</span><Link url="tel:0333336789" title="033 333 6789">033 333 6789</Link></div>
                <div><span>{t('footer.redesign.customerCare')}</span><Link url="tel:0333338939" title="0333338939">033 333 8939</Link></div>
                <div><span>{t('footer.redesign.partnership')}</span><Link url="tel:0333333789" title="0333333789">033 333 3789</Link></div>
                <div className={styles.email}><Image src="/email.svg" alt="" className={styles.smallIcon} objectFit="contain" /><Link url="mailto:support@anthu.tech">support@anthu.tech</Link></div>
              </div>
            </section>

            <FooterGroup id="footer-support-links" title={t('footer.title.support')}>
              <nav className={styles.links} aria-label={t('footer.title.support')}>
                <Link url={{ pathname: PagePath.SupportPage }}>{t('footer.redesign.support')}</Link>
                <Link url={{ pathname: PagePath.FaqPage }}>{t('footer.redesign.faq')}</Link>
                <Link url={{ pathname: '/tin-tuc/kien-thuc-trang-suc/' }}>{t('footer.redesign.knowledge')}</Link>
              </nav>
            </FooterGroup>

            <div>
              <FooterGroup id="footer-explore-links" title={t('footer.redesign.explore')}>
                <nav className={styles.links} aria-label={t('footer.redesign.explore')}>
                  <Link url={{ pathname: PagePath.AboutPage }}>{t('footer.redesign.about')}</Link>
                  <Link url={{ pathname: PagePath.ProductsPage }}>{t('footer.redesign.products')}</Link>
                  <Link url={{ pathname: PagePath.LiveVideosPage }}>{t('footer.redesign.live')}</Link>
                  <Link url="/tin-tuc/">{t('footer.redesign.news')}</Link>
                </nav>
              </FooterGroup>
              <h4 className={styles.socialTitle}>{t('footer.title.link')}</h4>
              <div className={styles.socialLinks}>
                {socialLinks.map((social) => <Link key={social.type} url={getSocialUrl(social.type)} target="_blank" title={social.name}><Image src={social.image} alt={social.name} className={styles.socialIcon} objectFit="contain" /></Link>)}
              </div>
            </div>

            <section className={styles.appCard} aria-labelledby="footer-app-title">
              <h3 id="footer-app-title" className={styles.appTitle}>{t('footer.title.download')}</h3>
              <p className={styles.appNote}>{t('footer.redesign.appNote')}</p>
              <div className={styles.appDownloads}>
                <Image src="/qr-code.jpg" alt={t('footer.redesign.appQr')} title={t('footer.redesign.appQr')} className={styles.qr} objectFit="contain" />
                <div className={styles.appBadges}>
                  <Link url={getSocialUrl(SocialType.AppleStore)} target="_blank" title="App Store"><Image src="/dl-appstore.png" alt="App Store" className={styles.storeBadge} objectFit="contain" /></Link>
                  <Link url={getSocialUrl(SocialType.GoogleStore)} target="_blank" title="Google Play"><Image src="/dl-ggplay.png" alt="Google Play" className={styles.storeBadge} objectFit="contain" /></Link>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className={styles.legalBand} style={legalStyle}>
          <div className={`container ${styles.legalContent}`}>
            <div className={styles.company}>
              <strong>© 2025 Công ty TNHH An Thư The Diamond Store</strong>
              <p className={styles.address}>Địa chỉ đăng ký trụ sở chính: <span>89A Nguyễn Trãi, Phường Bến Thành (Quận 1 cũ, gần chợ Bến Thành), Thành phố Hồ Chí Minh, Việt Nam</span></p>
              <div id="footer-company-details">
                <p>MST: <b>0314503621</b>, Ngày cấp: <b>07/07/2017</b>, Người đại diện: <b>Nguyễn Thành An</b></p>
                <p>Giấy chứng nhận ĐKKD <b>số 0314503621</b> do SKH&amp;ĐT TP. HCM cấp lần đầu ngày 07/07/2017, sửa đổi lần thứ 9 ngày 22/01/2025</p>
              </div>
            </div>
            <div className={styles.certification}>
              <span>{t('footer.title.certification')}</span>
              <Link url={getLinkBct(BctType.Announced)} target="_blank" title={t('footer.redesign.bct')}><Image src="/bct-2.png" alt={t('footer.redesign.bct')} objectFit="contain" className={styles.bctBadge} /></Link>
            </div>
          </div>
        </div>
      </footer>
      {isShowNav && platform === PlatForm.Web && (
        <div
          ref={mobileNavRef}
          className="tw-fixed md:tw-hidden tw-bottom-0 tw-w-full tw-bg-white tw-left-0 tw-z-55"
          // iPhone: chừa vùng thanh home / thanh Safari (cần viewport-fit=cover trong thẻ meta viewport)
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="tw-grid tw-grid-cols-6 tw-gap-4 tw-py-4 ">
            {mobileNavItems.map((item) => {
              const isActive = item.key === keyActive;
              return (
                <div key={item.title}>
                  <Link url={item.path}>
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
                      {!app_menu_icon ? (
                        <Icon
                          className=""
                          icon={isActive ? item.iconActive : item.icon}
                        />
                      ) : (
                        <Image
                          className='tw-w-6 tw-h-6'
                          objectFit='contain'
                          alt={item.title}
                          src={isActive ? item.themeIconActive : item.themeIcon}
                        />
                      )}
                      <span
                        className={`tw-mt-1 tw-text-10 ${
                          isActive ? 'tw-text-primary-main' : 'tw-text-gray-90'
                        }`}
                      >
                        {item.title}
                      </span>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default FooterPartial;
